import { useFormContext } from "react-hook-form";
import { mutate } from "swr";

import CertificationFields from "./fields/CertificationFields";
import OnboardingWizardStepFooter from "./OnboardingWizardStepFooter";
import OnboardingWizardStepHeader from "./OnboardingWizardStepHeader";
import type { WizardFormValues } from "./onboardingWizardForm";
import useOnboardingTracking from "./useOnboardingTracking";
import { useWizardMode } from "./WizardModeContext";
import { useOnboarding } from "~/hooks";

const OnboardingWizardStepCertification = () => {
	const { handleUpdate, setStep, setLearningPath } = useOnboarding();
	const { mode } = useWizardMode();
	const { track } = useOnboardingTracking();
	const {
		watch,
		formState: { isSubmitting },
	} = useFormContext<WizardFormValues>();

	const selected = watch("selectedCertificationId");

	const handleNext = async () => {
		if (!selected) return;
		setLearningPath("certification");
		if (mode === "live") {
			await handleUpdate({ selectedCertificationId: selected });
			await mutate("/certifications/selected");
		}
		track("onboarding:step_completed", { certification_id: selected });
		setStep(mode === "deferred" ? "auth" : "phone");
	};

	return (
		<div className="flex h-full min-h-0 flex-1 flex-col gap-4 phablet:gap-6">
			<OnboardingWizardStepHeader
				title="Which certification?"
				subtitle="Choose your learning path. You can switch at any time from your dashboard."
			/>

			<div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pr-1">
				<CertificationFields />

				<button
					type="button"
					onClick={() => {
						setLearningPath("ceu");
						setStep("ceu-goals");
					}}
					className="relative flex w-full shrink-0 cursor-pointer flex-col gap-1 rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-left transition-all hover:border-slate-300 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:hover:border-slate-600 dark:hover:bg-slate-700/60"
				>
					<span className="font-medium text-orange-700 text-sm dark:text-orange-300">
						Continuing Education (CEs)
					</span>
					<span className="font-semibold text-base text-black dark:text-white">
						Focus Only on Earning Continuing Education Credits
					</span>
					<span className="line-clamp-2 text-slate-700 text-sm dark:text-slate-100">
						&nbsp;
					</span>
				</button>
			</div>

			<OnboardingWizardStepFooter
				primary={{ onClick: handleNext, disabled: !selected || isSubmitting }}
			/>
		</div>
	);
};

export default OnboardingWizardStepCertification;
